import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { Service } from './use-services-catalog';
import { Database } from '@/integrations/supabase/types';

type WorkOrderItem = Database['public']['Tables']['work_order_items']['Row'];
type WorkOrder = Database['public']['Tables']['workbook_services']['Row'];

export interface WorkOrderWithItems extends WorkOrder {
  items: WorkOrderItem[];
}

export interface WorkOrdersData {
  workOrders: WorkOrderWithItems[];
}

export const useShoppingCart = (workbookId: string | undefined) => {
  const queryClient = useQueryClient();

  // Fetch all draft work orders
  const cartQuery = useQuery({
    queryKey: ['shopping-cart', workbookId],
    queryFn: async () => {
      if (!workbookId) return [];

      // Get all work orders for this workbook with their items (both draft and submitted)
      const { data: workOrders, error: workOrderError } = await supabase
        .from('workbook_services')
        .select(`
          *,
          items:work_order_items(*)
        `)
        .eq('workbook_id', workbookId)
        .in('work_order_status', ['draft', 'submitted'])
        .order('created_at', { ascending: false });

      if (workOrderError) {
        console.error('Error fetching work orders:', workOrderError);
        throw workOrderError;
      }

      // Return work orders with their items
      return workOrders || [];
    },
    enabled: !!workbookId,
  });

  // Create a new work order if none exists
  const createWorkOrder = async (workbookId: string, serviceName: string = 'New Work Order') => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('workbook_services')
      .insert({
        workbook_id: workbookId,
        service_name: serviceName,
        work_order_status: 'draft',
        added_by: user.id,
        total_amount: 0
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  // Add item to work order
  const addToCart = useMutation({
    mutationFn: async ({ 
      serviceId, 
      quantity = 1,
      customRequirements = null,
      workOrderId = null 
    }: { 
      serviceId: string; 
      quantity?: number;
      customRequirements?: string | null;
      workOrderId?: string | null;
    }) => {
      if (!workbookId) throw new Error('No workbook ID provided');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get service details from catalog
      const { data: service, error: serviceError } = await supabase
        .from('services')
        .select('*')
        .eq('id', serviceId)
        .single();

      if (serviceError) throw new Error('Service not found');

      // Use specified work order, get first work order, or create new one
      let workOrder;
      if (workOrderId) {
        // Use the specified work order
        const { data, error } = await supabase
          .from('workbook_services')
          .select('*')
          .eq('id', workOrderId)
          .single();
        if (error || !data) throw new Error('Work order not found');
        workOrder = data;
      } else {
        // Get first work order or create new one
        workOrder = cartQuery.data?.[0];
        if (!workOrder) {
          workOrder = await createWorkOrder(workbookId, 'Draft Work Order');
        }
      }

      // Check if this service already exists in the work order items
      // Note: Since we don't have service_id anymore, check by item_name
      const { data: existingItem } = await supabase
        .from('work_order_items')
        .select('id, quantity')
        .eq('workbook_service_id', workOrder.id)
        .eq('item_name', service.name)
        .single();

      if (existingItem) {
        // Update quantity if item exists
        const { error } = await supabase
          .from('work_order_items')
          .update({ 
            quantity: existingItem.quantity + quantity,
            custom_requirements: customRequirements 
          })
          .eq('id', existingItem.id);

        if (error) throw error;
        return { action: 'updated' };
      } else {
        // Add new item to work order
        const { error } = await supabase
          .from('work_order_items')
          .insert({
            workbook_service_id: workOrder.id,
            item_name: service.name,
            item_description: service.description,
            item_category: service.category,
            unit_price: service.base_price,
            quantity,
            currency_code: service.currency_code || 'USD',
            custom_requirements: customRequirements,
            is_custom_item: false,
            added_by: user.id,
          });

        if (error) throw error;
        return { action: 'added' };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['shopping-cart', workbookId] });
      toast({
        title: data.action === 'added' ? 'Added to Work Order' : 'Work Order Updated',
        description: data.action === 'added' 
          ? 'Service has been added to the work order'
          : 'Quantity has been updated',
      });
    },
    onError: (error) => {
      console.error('Error adding to work order:', error);
      toast({
        title: 'Error',
        description: 'Failed to add item to work order',
        variant: 'destructive',
      });
    },
  });

  // Update work order item
  const updateCartItem = useMutation({
    mutationFn: async ({ 
      itemId, 
      quantity,
      customRequirements 
    }: { 
      itemId: string; 
      quantity?: number;
      customRequirements?: string | null;
    }) => {
      const updates: any = {};
      if (quantity !== undefined) updates.quantity = quantity;
      if (customRequirements !== undefined) updates.custom_requirements = customRequirements;

      const { error } = await supabase
        .from('work_order_items')
        .update(updates)
        .eq('id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopping-cart', workbookId] });
    },
    onError: (error) => {
      console.error('Error updating cart item:', error);
      toast({
        title: 'Error',
        description: 'Failed to update cart item',
        variant: 'destructive',
      });
    },
  });

  // Remove item from work order
  const removeFromCart = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase
        .from('work_order_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopping-cart', workbookId] });
      toast({
        title: 'Removed from cart',
        description: 'Service has been removed from your cart',
      });
    },
    onError: (error) => {
      console.error('Error removing from cart:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove item from cart',
        variant: 'destructive',
      });
    },
  });

  // Clear entire work order (delete all items)
  const clearCart = useMutation({
    mutationFn: async () => {
      if (!workbookId || !cartQuery.data || cartQuery.data.length === 0) throw new Error('No work order to clear');

      // For now, clear items from first work order
      const { error } = await supabase
        .from('work_order_items')
        .delete()
        .eq('workbook_service_id', cartQuery.data[0].id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopping-cart', workbookId] });
      toast({
        title: 'Cart cleared',
        description: 'All items have been removed from your cart',
      });
    },
    onError: (error) => {
      console.error('Error clearing cart:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear cart',
        variant: 'destructive',
      });
    },
  });

  // Get all work orders
  const workOrders = cartQuery.data || [];
  
  // Calculate totals from all work orders
  const allItems = workOrders.flatMap(wo => wo.items || []);
  const cartTotals = {
    itemCount: allItems.reduce((sum, item) => sum + item.quantity, 0),
    subtotal: allItems.reduce((sum, item) => {
      return sum + (item.unit_price * item.quantity);
    }, 0),
  };

  return {
    workOrders,  // Return all work orders
    workOrder: workOrders[0] || null,  // Keep backward compatibility
    cartItems: allItems,
    isLoading: cartQuery.isLoading,
    addToCart: addToCart.mutate,
    updateCartItem: updateCartItem.mutate,
    removeFromCart: removeFromCart.mutate,
    clearCart: clearCart.mutate,
    cartTotals,
    isAddingToCart: addToCart.isPending,
    isUpdatingCart: updateCartItem.isPending,
    isRemovingFromCart: removeFromCart.isPending,
    isClearingCart: clearCart.isPending,
  };
};